import { useState, useCallback, useEffect } from 'react';
import { Calendar, Mail, Plus, Search, Trash2, UserPlus, Users, X } from 'lucide-react';
import {
  getPlatformUsers,
  removePlatformUser,
  createPlatformUser,
  getPlatformInvites,
  cancelPlatformInvite,
} from '../../../services/native-client-platform';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import {
  ConfirmDialog,
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '../../../components/ui/dialog';
import { EmptyState } from '../../../components/ui/empty-state';
import { Pagination } from '../../../components/ui/pagination';
import { Spinner } from '../../../components/ui/spinner';
import { toast } from 'sonner';
import { cn } from '../../../lib/cn';

const PER_PAGE = 10;

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SectionHeader({ icon: Icon, title, count, action }) {
  return (
    <div className="flex items-center justify-between px-1 pb-4">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{
            background: 'var(--dash-primary-soft)',
            border: '1px solid var(--dash-primary-border)',
            color: 'var(--dash-primary)',
          }}
        >
          <Icon size={16} strokeWidth={2.2} />
        </div>
        <h3
          className="text-sm font-semibold tracking-tight"
          style={{ color: 'var(--dash-text)' }}
        >
          {title}
        </h3>
        {count != null && (
          <Badge variant="default">{count}</Badge>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function UserRow({ user, onRemove }) {
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 transition-colors"
      style={{
        background: 'var(--dash-surface-subtle)',
        border: '1px solid var(--dash-border)',
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase"
          style={{
            background: 'var(--dash-primary-soft)',
            color: 'var(--dash-primary)',
            border: '1px solid var(--dash-primary-border)',
          }}
        >
          {user.name ? user.name.charAt(0) : user.email?.charAt(0) || '?'}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className="truncate text-sm font-medium"
            style={{ color: 'var(--dash-text)' }}
          >
            {user.name || '-'}
          </span>
          <span
            className="truncate text-xs"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            {user.email}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Badge variant={user.isActive ? 'success' : 'muted'} dot>
          {user.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
        <div
          className="hidden items-center gap-1.5 sm:flex"
          style={{ color: 'var(--dash-text-muted)' }}
        >
          <Calendar size={12} />
          <span className="text-[11px]">{formatDate(user.createdAt)}</span>
        </div>
        <Button
          variant="danger"
          size="icon-sm"
          onClick={() => onRemove(user)}
          title="Desassociar usuário"
        >
          <Trash2 size={14} strokeWidth={2.2} />
        </Button>
      </div>
    </div>
  );
}

function InviteRow({ invite, onCancel }) {
  const statusMap = {
    pending: { label: 'Pendente', variant: 'warning' },
    accepted: { label: 'Aceito', variant: 'success' },
    expired: { label: 'Expirado', variant: 'muted' },
  };

  const status = statusMap[invite.status] || {
    label: invite.status,
    variant: 'default',
  };

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 transition-colors"
      style={{
        background: 'var(--dash-surface-subtle)',
        border: '1px solid var(--dash-border)',
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{
            background: 'var(--dash-surface)',
            border: '1px solid var(--dash-border)',
            color: 'var(--dash-text-muted)',
          }}
        >
          <Mail size={15} strokeWidth={2} />
        </div>
        <span
          className="truncate text-sm font-medium"
          style={{ color: 'var(--dash-text)' }}
        >
          {invite.email}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <Badge variant={status.variant} dot>
          {status.label}
        </Badge>
        <div
          className="hidden items-center gap-1.5 sm:flex"
          style={{ color: 'var(--dash-text-muted)' }}
        >
          <Calendar size={12} />
          <span className="text-[11px]">{formatDate(invite.expiresAt)}</span>
        </div>
        {invite.status === 'pending' && (
          <Button
            variant="danger"
            size="icon-sm"
            onClick={() => onCancel(invite)}
            title="Cancelar convite"
          >
            <X size={14} strokeWidth={2.2} />
          </Button>
        )}
      </div>
    </div>
  );
}

function InviteDialog({ open, onClose, onSubmit, loading }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setEmail('');
      setError('');
    }
  }, [open]);

  const validate = () => {
    if (!email.trim()) {
      setError('Por favor, insira o e-mail do usuário.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Insira um e-mail válido.');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    onSubmit({ email: email.trim() });
  };

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogHeader
        icon={UserPlus}
        title="Convidar Usuário"
        description="Um convite será enviado para o e-mail informado."
      />
      <DialogContent>
        <form onSubmit={handleSubmit} id="invite-form">
          <label
            className="mb-1.5 block text-xs font-medium"
            style={{ color: 'var(--dash-text-soft)' }}
          >
            E-mail
          </label>
          <Input
            type="email"
            placeholder="Ex: joao@exemplo.com"
            leftIcon={Mail}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            invalid={!!error}
            autoFocus
          />
          {error && (
            <p className="mt-1.5 text-xs" style={{ color: '#d61f3f' }}>
              {error}
            </p>
          )}
        </form>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="invite-form"
          loading={loading}
          leftIcon={Plus}
        >
          Convidar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default function UsersTab({ platformId }) {
  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);

  // Invites state
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesTotal, setInvitesTotal] = useState(0);
  const [invitesPage, setInvitesPage] = useState(1);

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Confirm dialogs
  const [removeConfirm, setRemoveConfirm] = useState({ open: false, user: null });
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, invite: null });

  // ─── Fetch ────────────────────────────────────────────────
  const fetchUsers = useCallback(
    (page = 1) => {
      setUsersLoading(true);
      getPlatformUsers(platformId, { page, perPage: PER_PAGE })
        .then((res) => {
          setUsers(res.data || []);
          setUsersTotal(res.total || 0);
        })
        .catch((e) => {
          console.error(e);
          toast.error('Erro ao carregar usuários.');
        })
        .finally(() => setUsersLoading(false));
    },
    [platformId],
  );

  const fetchInvites = useCallback(
    (page = 1) => {
      setInvitesLoading(true);
      getPlatformInvites(platformId, { page, perPage: PER_PAGE })
        .then((res) => {
          setInvites(res.data || []);
          setInvitesTotal(res.total || 0);
        })
        .catch((e) => {
          console.error(e);
          toast.error('Erro ao carregar convites.');
        })
        .finally(() => setInvitesLoading(false));
    },
    [platformId],
  );

  useEffect(() => {
    if (platformId) fetchUsers(usersPage);
  }, [fetchUsers, usersPage, platformId]);

  useEffect(() => {
    if (platformId) fetchInvites(invitesPage);
  }, [fetchInvites, invitesPage, platformId]);

  // ─── Actions ──────────────────────────────────────────────
  const handleInviteSubmit = (values) => {
    setInviteLoading(true);
    createPlatformUser(platformId, values)
      .then(() => {
        toast.success('Convite enviado com sucesso!');
        setInviteOpen(false);
        fetchUsers(usersPage);
        fetchInvites(invitesPage);
      })
      .catch((e) => {
        console.error(e);
        const msg = e?.response?.data?.message || 'Erro ao enviar convite.';
        toast.error(msg);
      })
      .finally(() => setInviteLoading(false));
  };

  const handleRemoveUser = () => {
    const user = removeConfirm.user;
    if (!user) return;
    removePlatformUser(platformId, user.id)
      .then(() => {
        toast.success('Usuário desvinculado da plataforma.');
        setRemoveConfirm({ open: false, user: null });
        fetchUsers(usersPage);
      })
      .catch((e) => {
        console.error(e);
        toast.error('Erro ao desvincular usuário.');
      });
  };

  const handleCancelInvite = () => {
    const invite = cancelConfirm.invite;
    if (!invite) return;
    cancelPlatformInvite(platformId, invite.id)
      .then(() => {
        toast.success('Convite cancelado.');
        setCancelConfirm({ open: false, invite: null });
        fetchInvites(invitesPage);
      })
      .catch((e) => {
        console.error(e);
        toast.error('Erro ao cancelar convite.');
      });
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-8">
      {/* ── Seção 1: Usuários ativos ─────────────────────────── */}
      <Card className="overflow-hidden p-6">
        <SectionHeader
          icon={Users}
          title="Usuários"
          count={usersTotal}
          action={
            <Button
              variant="primary"
              size="sm"
              leftIcon={UserPlus}
              onClick={() => setInviteOpen(true)}
            >
              Convidar
            </Button>
          }
        />

        {usersLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex animate-pulse items-center gap-4 rounded-xl p-4" style={{ background: "var(--dash-surface-subtle)", border: "1px solid var(--dash-border)", animationDelay: `${i * 70}ms` }}>
                <div className="h-9 w-9 rounded-full" style={{ background: "var(--dash-surface-hover)" }} />
                <div className="flex flex-1 flex-col gap-1.5">
                  <div className="h-3.5 rounded" style={{ background: "var(--dash-surface-hover)", width: [120, 160, 140, 180][i % 4] }} />
                  <div className="h-2.5 w-32 rounded" style={{ background: "var(--dash-surface-hover)" }} />
                </div>
                <div className="h-5 w-12 rounded-full" style={{ background: "var(--dash-surface-hover)" }} />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum usuário"
            description="Convide usuários para esta plataforma."
            action={
              <Button
                variant="primary"
                size="sm"
                leftIcon={UserPlus}
                onClick={() => setInviteOpen(true)}
              >
                Convidar usuário
              </Button>
            }
          />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  onRemove={(u) => setRemoveConfirm({ open: true, user: u })}
                />
              ))}
            </div>
            <Pagination
              page={usersPage}
              pageSize={PER_PAGE}
              total={usersTotal}
              onPageChange={setUsersPage}
              className="mt-2"
            />
          </>
        )}
      </Card>

      {/* ── Seção 2: Convites pendentes ──────────────────────── */}
      <Card className="overflow-hidden p-6">
        <SectionHeader
          icon={Mail}
          title="Convites"
          count={invitesTotal}
        />

        {invitesLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex animate-pulse items-center gap-4 rounded-xl p-4" style={{ background: "var(--dash-surface-subtle)", border: "1px solid var(--dash-border)", animationDelay: `${i * 70}ms` }}>
                <div className="h-9 w-9 rounded-full" style={{ background: "var(--dash-surface-hover)" }} />
                <div className="flex flex-1 flex-col gap-1.5">
                  <div className="h-3.5 w-40 rounded" style={{ background: "var(--dash-surface-hover)" }} />
                  <div className="h-2.5 w-20 rounded" style={{ background: "var(--dash-surface-hover)" }} />
                </div>
                <div className="h-5 w-14 rounded-full" style={{ background: "var(--dash-surface-hover)" }} />
              </div>
            ))}
          </div>
        ) : invites.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="Nenhum convite"
            description="Os convites enviados aparecerão aqui."
          />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {invites.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  onCancel={(inv) => setCancelConfirm({ open: true, invite: inv })}
                />
              ))}
            </div>
            <Pagination
              page={invitesPage}
              pageSize={PER_PAGE}
              total={invitesTotal}
              onPageChange={setInvitesPage}
              className="mt-2"
            />
          </>
        )}
      </Card>

      {/* ── Dialogs ──────────────────────────────────────────── */}
      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInviteSubmit}
        loading={inviteLoading}
      />

      <ConfirmDialog
        open={removeConfirm.open}
        onClose={() => setRemoveConfirm({ open: false, user: null })}
        onConfirm={handleRemoveUser}
        title="Desvincular usuário?"
        description={`O usuário "${removeConfirm.user?.name || removeConfirm.user?.email || ''}" será removido desta plataforma.`}
        confirmLabel="Desvincular"
        variant="danger"
      />

      <ConfirmDialog
        open={cancelConfirm.open}
        onClose={() => setCancelConfirm({ open: false, invite: null })}
        onConfirm={handleCancelInvite}
        title="Cancelar convite?"
        description={`O convite para "${cancelConfirm.invite?.email || ''}" será cancelado.`}
        confirmLabel="Cancelar convite"
        variant="danger"
      />
    </div>
  );
}
